import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { CircleTasksCard } from "@/components/cember/CircleTasksCard";
import { Button } from "@/components/ui/button";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { getCircleMemberRole, listMyCircles, type CircleSummary } from "@/lib/supabase/circles";

type CircleWithRole = CircleSummary & { myRole: "member" | "admin" };

export default function UserPanelCemberGorevler() {
  const { user, loading } = useSupabaseSession();
  const userId = String(user?.id ?? "");

  const circlesQuery = useQuery({
    queryKey: ["panel-circles-mine-with-role", userId],
    enabled: Boolean(userId) && !loading,
    staleTime: 10_000,
    queryFn: async (): Promise<CircleWithRole[]> => {
      const circles = await listMyCircles(userId);
      const roles = await Promise.all(circles.map((c) => getCircleMemberRole({ circleId: c.id, userId })));
      return circles.map((c, idx) => ({ ...c, myRole: roles[idx] ?? "member" }));
    },
  });

  const circles = useMemo(() => {
    const byId = new Map<string, CircleWithRole>();
    for (const c of circlesQuery.data ?? []) {
      if (!c?.id) continue;
      if (!byId.has(c.id)) byId.set(c.id, c);
    }

    // If multiple circles have a visually identical name (different accents/spaces), show only one.
    const byName = new Map<string, CircleWithRole>();
    for (const c of byId.values()) {
      const key = String(c.name ?? "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLocaleLowerCase("tr-TR")
        .replace(/\s+/g, "")
        .replace(/[ç]/g, "c")
        .replace(/[ğ]/g, "g")
        .replace(/[ı]/g, "i")
        .replace(/[ö]/g, "o")
        .replace(/[ş]/g, "s")
        .replace(/[ü]/g, "u")
        .replace(/[^0-9a-z]+/g, "");

      if (!byName.has(key)) byName.set(key, c);
    }

    return Array.from(byName.values());
  }, [circlesQuery.data]);

  if (loading) {
    return <div className="text-sm text-slate-500">Yükleniyor...</div>;
  }

  if (!userId) {
    return <div className="text-sm text-slate-500">Günün görevlerini görmek için giriş yapmalısın.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">Günün Görevleri</div>
          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">Üye olduğun tüm çemberlerin günlük görevleri</div>
        </div>
        <Button asChild variant="outline" className="rounded-xl">
          <Link to="/panel/cember">Çemberlere dön</Link>
        </Button>
      </div>

      {circlesQuery.isLoading ? (
        <div className="text-sm text-slate-500 dark:text-slate-400">Çemberler yükleniyor...</div>
      ) : circles.length ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {circles.map((c) => {
            const canManage = c.ownerId === userId || c.myRole === "admin";
            return <CircleTasksCard key={c.id} circleId={c.id} circleName={c.name} canManage={canManage} />;
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
          Henüz üye olduğun bir çember yok.
        </div>
      )}
    </div>
  );
}
